import {
  getAssignedSalesManagers,
  ZohoUserAssignmentPageKey,
} from '@internal/services/TSalesManager'
import { useFeatureFlag } from '@internal/utils/featureFlags/useFeatureFlag'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { isLeasingQualifiedCountry } from '@internal/utils/machine/leasing'
import { UserContext } from '@internal/utils/user/UserContext'
import { KeyboardArrowDown } from '@mui/icons-material'
import { Button, MobileOverlay } from '@renderer-ui-library/atoms'
import {
  DynamicScrollToTopButton,
  FilterMenuBar,
} from '@renderer-ui-library/molecules'
import { SearchResultsTitle } from '@renderer-ui-library/molecules/SearchResultsTitle/SearchResultsTitle'
import { Box, Grid } from '@renderer-ui-library/mui'
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery'
import {
  CustomerMap,
  LeasingQualifiedContextProvider,
  LeasingQualifiedFilter,
  SearchFilterBar,
} from '@renderer-ui-library/organisms'
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper'
import { BlockProps } from 'blocks/BlockProps'
import classNames from 'classnames'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { SalesManagerContext } from 'utils/SalesManager/SalesManagerContext'
import { tracker } from 'utils/tracking/tracker'
import { CardBannerBlock } from '../CardBannerBlock/CardBannerBlock'
import { CompareMachinesAlerts } from './CompareMachines/CompareMachinesAlerts'
import { CompareMachinesOverlay } from './CompareMachines/CompareMachinesOverlay'
import { MachineComparisonContextProvider } from './CompareMachines/MachineComparisonContextProvider'
import { CompareMachinesButton } from './CompareMachinesButton/CompareMachinesButton'
import FilterChips from './FilterChips'
import { GhostResultsList } from './GhostResultsList'
import { ISearchResultsBlockData } from './ISearchResultsBlockData'
import { LoaderOverlay } from './LoaderOverlay/LoaderOverlay'
import { NoResults } from './NoResults'
import { ResultsList } from './ResultsList'
import { SearchResultsSorting } from './SearchResultsSorting'
import { TServerSideSearchResultsBlockProps } from './TServerSideSearchResultsBlockProps'
import { loadChunkSize } from './loadChunkSize'
import {
  aboveMaxSearchResultsLabel,
  maxSearchResults,
} from './maxSearchResults'
import styles from './searchResultsBlock.module.scss'
import { useSearchParams } from './useSearchParams'
import { useSearchResults } from './useSearchResults'
import { useSearchResultsRouting } from './useSearchResultsRouting'

type TMobileMenuContent = 'filter' | 'sort' | undefined
export type SearchResultsBlockProps = BlockProps<
  ISearchResultsBlockData,
  TServerSideSearchResultsBlockProps
>

export const SearchResultsBlock: React.FC<SearchResultsBlockProps> = React.memo(
  (props) => {
    const searchParams = useSearchParams()
    const { machines, resultsCount, isLoading, isServingFromCache } =
      useSearchResults()

    const { hasFeature } = useFeatureFlag()
    const isChipsFeatureEnabled = hasFeature('chips')
    const { salesManagers } = useContext(SalesManagerContext)
    const isZohoUsersFeatureEnabled = hasFeature('zohoUsers')
    const salesManager = useMemo(() => {
      if (!isZohoUsersFeatureEnabled) {
        return salesManagers[0] ?? null
      }
      return (
        getAssignedSalesManagers(
          salesManagers,
          ZohoUserAssignmentPageKey.ProductDetails
        )[0] ?? null
      )
    }, [isZohoUsersFeatureEnabled, salesManagers])
    const { t } = useTranslation(localeNamespace.common)
    const { gotoSearchResults } = useSearchResultsRouting()
    const [mobileMenuContent, setMobileMenuContent] =
      useState<TMobileMenuContent>()
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
    const { amount, brands, model, machineType } = searchParams
    const { popularBrands, popularModels } = props.preFetchedBlockProps

    const { user } = useContext(UserContext)
    const showLeasingFilter = isLeasingQualifiedCountry(user.countryCode)
    const { isMobile } = useMediaQuery()

    const handleLoadMoreClick = useCallback(
      () =>
        gotoSearchResults({
          ...searchParams,
          amount: machines.length + loadChunkSize,
          replace: true,
        }),
      [gotoSearchResults, searchParams, machines.length]
    )

    useEffect(() => {
      if (!hasLoadedOnce && (!isLoading || isServingFromCache)) {
        setHasLoadedOnce(true)
      }
    }, [isLoading, hasLoadedOnce, isServingFromCache])

    useEffect(() => {
      tracker.trackEvent({
        name: 'ProductImpressions',
        action: 'listing-tag',
        ecommerce: {
          impressions: machines.map(({ data }) => ({
            id: data.MACHINE_GROUP_ID.toLowerCase(),
            category: data.MACHINE_TYPE,
          })),
        },
      })
    }, [brands, gotoSearchResults, searchParams, machines, popularModels])

    const showGhostCards = !hasLoadedOnce && isLoading && machines.length === 0
    const showNoResultsTips = !isLoading && machines.length === 0
    const isEnabled = !!(machineType && !isLoading && machines.length > 0)
    const showLoadMoreButton = machines.length < resultsCount

    const scrollUp = useCallback(() => {
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0
      }
    }, [])

    const handleFilterClick = useCallback(
      () => setMobileMenuContent('filter'),
      [setMobileMenuContent]
    )
    const handleSortClick = useCallback(
      () => setMobileMenuContent('sort'),
      [setMobileMenuContent]
    )

    const closeMobileMenu = useCallback(
      () => setMobileMenuContent(undefined),
      [setMobileMenuContent]
    )

    const handleApplyClick = useCallback(() => {
      scrollUp()
      closeMobileMenu()
    }, [closeMobileMenu, scrollUp])

    const handleResetClick = useCallback(() => {
      scrollUp()
      gotoSearchResults({
        machineType: searchParams.machineType,
        sort: searchParams.sort,
      })
      closeMobileMenu()
    }, [
      scrollUp,
      gotoSearchResults,
      searchParams.machineType,
      searchParams.sort,
      closeMobileMenu,
    ])

    return (
      <MachineComparisonContextProvider machineType={machineType}>
        <LeasingQualifiedContextProvider>
          <>
            <BlockWrapper blockData={props.data} noSpacingTop>
              <Grid container columnSpacing={2}>
                <Grid item xs={12}>
                  <SearchResultsTitle
                    brands={brands}
                    model={model?.name}
                    resultsCount={resultsCount}
                    machineType={machineType}
                    popularBrands={popularBrands}
                    popularModels={popularModels}
                    seoDescription={props.seoDescription}
                    currentModel={searchParams.model?.name}
                    hasLoadedOnce={hasLoadedOnce}
                  />
                </Grid>
                <Grid item xs={false} md={3}>
                  <SearchFilterBar
                    type='desktop'
                    config={props.preFetchedBlockProps.filters}
                    machineType={searchParams.machineType}
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  {isChipsFeatureEnabled && <FilterChips />}
                  <div className={styles.sort}>
                    <SearchResultsSorting closeMobileMenu={closeMobileMenu} />
                    <div className={styles.sortWrapper}>
                      {showLeasingFilter && <LeasingQualifiedFilter />}
                      <CompareMachinesButton
                        enabled={isEnabled}
                        machineType={machineType}
                      />

                      {isEnabled && (
                        <CompareMachinesOverlay locale={props.locale} />
                      )}
                    </div>
                  </div>

                  <div className={styles.resultsWrapper}>
                    <Grid
                      container
                      columnSpacing={2}
                      rowSpacing={{
                        xs: 3,
                        lg: 4,
                      }}
                      className={styles.results}
                    >
                      <FilterMenuBar
                        onFilterClick={handleFilterClick}
                        onSortClick={handleSortClick}
                        machineType={machineType}
                      />
                      {showNoResultsTips && (
                        <NoResults
                          salesManager={salesManager}
                          machineType={machineType}
                          locale={props.locale}
                        />
                      )}
                      {showGhostCards && <GhostResultsList amount={amount} />}
                      <ResultsList
                        machines={machines.slice(0, 6)}
                        locale={props.locale}
                        services={props.data.services}
                      />
                      {!showGhostCards &&
                        !showNoResultsTips &&
                        props.cardBannerBlock && (
                          <Grid item xs={12}>
                            <CardBannerBlock
                              {...props}
                              data={props.cardBannerBlock}
                            />
                          </Grid>
                        )}
                      <ResultsList
                        machines={machines.slice(6, 12)}
                        locale={props.locale}
                        services={props.data.services}
                      />

                      {!showGhostCards && !showNoResultsTips && (
                        <Grid item xs={12}>
                          <CustomerMap
                            locale={props.locale}
                            mapWidth={isMobile ? 600 : 876}
                          />
                        </Grid>
                      )}

                      <ResultsList
                        machines={machines.slice(12, -1)}
                        locale={props.locale}
                        services={props.data.services}
                      />

                      <LoaderOverlay
                        className={styles.loaderOverlay}
                        show={isLoading && !isServingFromCache}
                      />
                    </Grid>
                  </div>

                  {showLoadMoreButton && (
                    <Box className={styles.moreButton}>
                      <Button
                        onClick={handleLoadMoreClick}
                        variant='contained'
                        size='large'
                        endIcon={<KeyboardArrowDown />}
                        disabled={isLoading}
                        color='secondary'
                      >
                        {t(translations.loadMoreMachinesButtonLabel)}
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
              <div
                className={classNames(styles.mobileFilterBar, {
                  [styles.mobileFilterBarOpen]: !!mobileMenuContent,
                })}
              >
                <MobileOverlay
                  open={!!mobileMenuContent}
                  title={
                    mobileMenuContent === 'filter'
                      ? t(translations.searchResultsFiltersTitle)
                      : t(translations.searchResultsSortTitle)
                  }
                  onCloseClick={closeMobileMenu}
                >
                  {mobileMenuContent === 'filter' ? (
                    <SearchFilterBar
                      type='mobile'
                      config={props.preFetchedBlockProps.filters}
                      machineType={searchParams.machineType}
                      onClickApply={handleApplyClick}
                      onClickReset={handleResetClick}
                    />
                  ) : (
                    <SearchResultsSorting
                      onChange={scrollUp}
                      closeMobileMenu={closeMobileMenu}
                    />
                  )}
                </MobileOverlay>
              </div>
              <DynamicScrollToTopButton
                label={
                  hasLoadedOnce
                    ? `${t(translations.searchResultsScrollToTopButtonLabel, {
                        count: resultsCount,
                        amount:
                          resultsCount >= maxSearchResults
                            ? aboveMaxSearchResultsLabel
                            : resultsCount,
                      })}`
                    : undefined
                }
              />
              <CompareMachinesAlerts locale={props.locale} />
            </BlockWrapper>
          </>
        </LeasingQualifiedContextProvider>
      </MachineComparisonContextProvider>
    )
  }
)

SearchResultsBlock.displayName = 'SearchResultsBlock'
